import {AnimatePresence, motion} from 'framer-motion'
import {useEffect, useState} from 'react'

import {DirectionEnum, noop, twMerge, wrap} from '@sdox/utils'

import {EnterExitVariants} from '../../animations'
import {Thumbnails} from './Thumbnails'
import {ArrowControl} from './ArrowControl'

import type {ReactNode} from 'react'
import type {DragHandlers, MotionProps} from 'framer-motion'
import type {ClassNameValue} from 'tailwind-merge'

interface CarouselProps {
  className?: ClassNameValue
  children?: ReactNode
  initialIndex?: number
  images?: string[]
  preload?: boolean
  withArrows?: boolean
  shouldHandleArrowKeys?: boolean
  altPrefix?: string
  withRotationHandling?: boolean
  withSwipeHandling?: boolean
  withThumbnails?: boolean
  rotationDelta?: number
  imgMotionProps?: MotionProps
}

const swipeConfidenceThreshold = 10
const swipePower = (offset: number, velocity: number) => Math.abs(offset) * velocity

export const Carousel = ({
  className,
  children,
  initialIndex = 0,
  images = [],
  preload = true,
  withArrows = true,
  shouldHandleArrowKeys = false,
  withRotationHandling = false,
  withSwipeHandling = true,
  withThumbnails = false,
  rotationDelta = 1,
  altPrefix = 'carousel',
  imgMotionProps,
}: CarouselProps) => {
  const [[current, direction], setCurrent] = useState([initialIndex, 0])
  const [isGrabbing, setIsGrabbing] = useState(false)

  useEffect(() => {
    setCurrent([initialIndex, 0])
  }, [initialIndex])

  const imageIndex = wrap(0, images.length, current)

  const handleDirection = (newDirection: number) => setCurrent(([_current]) => [_current + newDirection, newDirection])

  useEffect(() => {
    if (!shouldHandleArrowKeys) return

    const handleArrowKeys = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') handleDirection(1)
      if (event.key === 'ArrowLeft') handleDirection(-1)
    }

    document.addEventListener('keydown', handleArrowKeys)
    return () => {
      document.removeEventListener('keydown', handleArrowKeys)
    }
  }, [shouldHandleArrowKeys])

  const handleRotate: DragHandlers['onDrag'] = (e, {delta}) => {
    if (Math.abs(delta.x) > rotationDelta) {
      handleDirection(delta.x > 0 ? 1 : -1)
    }
  }

  const handleSwipe: DragHandlers['onDragEnd'] = (e, {offset, velocity}) => {
    const swipe = swipePower(offset.x, velocity.x)

    if (swipe < -swipeConfidenceThreshold) {
      handleDirection(1)
    } else if (swipe > swipeConfidenceThreshold) {
      handleDirection(-1)
    }
  }

  return (
    <div
      className={twMerge(
        'relative grid overflow-hidden',
        withThumbnails ? 'grid-rows-[calc(100%-84px)_80px] gap-spacing-4' : 'grid-rows-1',
        className,
      )}
    >
      <div className={isGrabbing ? 'cursor-grabbing' : 'cursor-grab'}>
        {withArrows ? (
          <ArrowControl
            direction={DirectionEnum.LEFT}
            onClick={() => handleDirection(-1)}
          />
        ) : null}
        <div>
          {preload && images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`preload_${index}`}
              hidden
            />
          ))}
        </div>
        <AnimatePresence
          initial={false}
          mode={'popLayout'}
          custom={direction}
        >
          <motion.img
            key={current}
            src={images[imageIndex]}
            alt={`${altPrefix}_${current}`}
            className={'relative size-full select-none object-contain object-center'}
            custom={direction}
            variants={EnterExitVariants({
              none: '0%',
              right: '125%',
              left: '-125%',
            })}
            initial={'enter'}
            animate={'center'}
            exit={'exit'}
            transition={{
              x: {type: 'spring', stiffness: 300, damping: 30},
            }}
            drag={'x'}
            dragConstraints={{left: 0, right: 0}}
            dragElastic={1}
            onMouseDown={() => setIsGrabbing(true)}
            onMouseUp={() => setIsGrabbing(false)}
            onDragEnd={(e, info) => withSwipeHandling ? handleSwipe(e, info) : noop}
            onDrag={(e, info) => withRotationHandling ? handleRotate(e, info) : noop}
            {...imgMotionProps}
          />
        </AnimatePresence>
        {withArrows ? (
          <ArrowControl
            direction={DirectionEnum.RIGHT}
            onClick={() => handleDirection(1)}
          />
        ) : null}
      </div>
      {withThumbnails ? (
        <Thumbnails
          images={images}
          imageIndex={imageIndex}
          onClick={(i) => setCurrent([i, 0])}
        />
      ) : null}
      {children}
    </div>
  )
}
